import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

const PayoutDisputesHomeRehabArticle = () => (
    <Box>
        <Typography variant="h3" component="h1" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.65rem', md: '2rem' } }}>
            Payout Disputes in Home Rehab: Can Your System Fix Them?
        </Typography>
        <Typography color="text.secondary" fontWeight={600} paragraph sx={{ fontStyle: 'italic' }}>
            No record, no clean payment cycle.
        </Typography>
        <Typography color="text.secondary" paragraph>
            TL;DR: Payout disputes don't keep happening because your therapists are dishonest. They keep happening because
            there's no verified record either side can point to. Trust-based reporting works at two or three therapists. It
            breaks at eight. The question isn't whether to fix it. It's whether your current system is actually capable of
            fixing it.
        </Typography>
        <Typography color="text.secondary" paragraph>
            A therapist says she completed 22 sessions this month. Your count shows 19. Neither of you is wrong, exactly. But
            neither of you can prove anything either.
        </Typography>
        <Typography color="text.secondary" paragraph>
            You pay the 22. Or you push back and spend the next two weeks managing a resentful therapist while your admin
            tries to reconstruct what actually happened from a WhatsApp group and a spreadsheet nobody updated consistently.
            Either way, the month ends in compromise. And the same conversation is waiting for you next month.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Most centre owners who get here start wondering whether they hired the wrong people. Whether they need a stricter
            reporting process. Whether more frequent check-ins would help. These are reasonable instincts. They don't fix the
            problem.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The problem isn't the people. It's that there's no verified record of what sessions actually took place.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            Why the dispute keeps repeating
        </Typography>
        <Typography color="text.secondary" paragraph>
            When therapists self-report their sessions and there's no independent confirmation, the payment cycle runs
            entirely on trust. That works at a very small scale - two or three therapists you've known for years, a manageable
            caseload, everyone in regular contact. It stops working as the team grows.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Take Anand, who runs a 12-therapist rehab centre in Bengaluru. He knows his team. He trusts most of them. But by
            the tenth therapist, he stopped being able to personally verify anything. Every payment cycle, someone's count
            doesn't match his admin's count. Not by much. But enough. By the time it's resolved, half a Tuesday is gone and
            nobody feels good about the number they landed on.
        </Typography>
        <Typography color="text.secondary" paragraph>
            And it's not just session counts. Travel claims are just as hard to verify. A therapist covering six patients
            across the city can claim distances your admin has no way to cross-check without a verified record of where they
            actually went.
        </Typography>
        <Typography color="text.secondary" paragraph>
            At eight or ten therapists delivering home visits across different parts of the city, you can't personally verify
            anything. You're dependent on what people tell you. And when the numbers don't match, there's no record to go back
            to. Just two accounts of what happened, both incomplete.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The financial leakage from this is real. Small discrepancies every month, each one resolved through negotiation
            rather than calculation, add up. But the harder cost is the friction - the hours your admin spends chasing
            documentation, the conversations that end in compromise rather than clarity, the slow erosion of trust between
            management and field staff that comes from the same dispute playing out repeatedly.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What does a verified session record actually look like?
        </Typography>
        <Typography color="text.secondary" paragraph>
            It captures who delivered the session, which patient was seen, when, and where - completed at the point of delivery,
            not filled in later from memory.
        </Typography>
        <Typography color="text.secondary" paragraph>
            A message saying 'done, moving to next patient' is not a verified record. Neither is a spreadsheet entry filled in
            on Friday afternoon from memory. Both depend on the therapist's recall and goodwill. Both can be disputed.
        </Typography>
        <Typography color="text.secondary" paragraph>
            A record created at the moment of delivery, with the required fields completed on-site, cannot be disputed in the
            same way. The session either happened or it didn't, and the record reflects that. The fields aren't optional.
            Structured documentation isn't a compliance checkbox. It's what makes the record defensible when a dispute or
            audit surfaces.
        </Typography>
        <Typography color="text.secondary" paragraph>
            When that record exists, the payout calculation changes. The figure comes from confirmed data, not from matching
            two separate accounts after the fact. There's no reconciliation step because there's nothing to reconcile.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Also read:{' '}
            <Link to="/blog/session-verification-payout-cycle-home-rehab">
                How Session Verification Changes Your Payout Cycle
            </Link>
            .
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What changes at month-end when verified records exist?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Month-end stops being an investigation and becomes a calculation. The data is already there before anyone starts
            asking questions.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Without verified records, month-end is an investigation. Someone has to pull together whatever exists, chase the
            gaps, and arrive at a number both sides are willing to accept. With verified records, it's a calculation. The
            data's already there.
        </Typography>
        <Typography color="text.secondary" paragraph>
            If a therapist has a question about their payout, the conversation is short - here's the record for each session,
            here's what was captured, here's where the count comes from. Questions get resolved by looking at the record, not
            by asking someone to recall what they did three weeks ago.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Centres on meDZhub, which creates a verified record at point of delivery and links it directly to payout
            calculation, report zero payout disputes across 35+ therapists. The number stops being contested when it becomes a
            confirmed fact rather than a claim.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/blog/when-home-therapy-teams-grow-what-breaks-at-scale">
                When Home Therapy Teams Grow: What Breaks at Scale
            </Link>
            .
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            Why WhatsApp and spreadsheets can't close this gap
        </Typography>
        <Typography color="text.secondary" paragraph>
            The instinct when disputes start is to add a layer - a new reporting format, a daily check-in message, a more
            detailed spreadsheet. These reduce ambiguity at the margins. They don't fix the underlying problem.
        </Typography>
        <Typography color="text.secondary" paragraph>
            WhatsApp was built for communication, not documentation. A message thread is evidence of a conversation, not
            evidence of a delivery. Spreadsheets have the same problem. They're as accurate as the person filling them in, as
            timely as that person remembers, and as complete as whatever fields someone decided to include. Neither produces a
            verified record. Both depend entirely on the therapist's recall and timing. When a claim gets rejected or a
            payout is disputed, neither gives you what you need.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Fragments aren't records. They're just less ambiguous guesses. Adding a stricter reporting process on top of this
            doesn't fix it. It just gives you something to point at when the next dispute happens.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/blog/why-whatsapp-breaks-field-operations-system">
                Why WhatsApp Breaks as a Field Operations System
            </Link>
            .
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            How do you know if your current system can actually fix this?
        </Typography>
        <Typography color="text.secondary" paragraph>
            One question cuts through it: does your system create a verified record at the point of delivery, or does it
            collect and match data after the fact?
        </Typography>
        <Typography color="text.secondary" paragraph>
            If your answer is the second one, the reconciliation problem doesn't go away. It just gets a slightly better
            process wrapped around it.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Ask three questions about how your current setup works:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2, color: 'text.secondary' }}>
            <Box component="li" sx={{ mb: 0.5 }}>
                When is the session record created - at delivery, or later?
            </Box>
            <Box component="li" sx={{ mb: 0.5 }}>
                Who creates it - the therapist alone, or does the system capture it independently?
            </Box>
            <Box component="li" sx={{ mb: 0.5 }}>
                Does the payout figure come directly from that record, or is there a manual step between the record and the
                number?
            </Box>
        </Box>
        <Typography color="text.secondary" paragraph>
            If the record is created after the fact, by the therapist, with a manual step before it reaches payroll, the
            dispute risk stays. Reported data is not verified data. The process gets tidier. The problem doesn't go away.
        </Typography>
        <Typography color="text.secondary" paragraph>
            A system that closes this gap does all three: captures the session record at the point of delivery, without
            depending entirely on therapist input, and feeds that record directly into payout calculation with no
            reconciliation step in between.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            How meDZhub handles this
        </Typography>
        <Typography color="text.secondary" paragraph>
            Most tools in this space record what someone typed. meDZhub creates a verified record of what actually happened.
            Who delivered the session, at which patient's home, when. That record is not dependent on therapist self-reporting,
            and it feeds directly into payout calculation. The figure is calculated automatically from confirmed session data,
            not entered manually.
        </Typography>
        <Typography color="text.secondary" paragraph>
            There is no reconciliation step because there is nothing to reconcile. The payout comes from confirmed data, not
            from matching two separate accounts after the fact.
        </Typography>
        <Typography color="text.secondary" paragraph>
            It's the only system in India built specifically for this workflow - home rehabilitation field operations, not
            adapted from a clinic tool.
        </Typography>
        <Typography color="text.secondary" paragraph>
            If your payment cycles still end in negotiation, it's worth seeing how meDZhub works.
        </Typography>
        <Button component={Link} to="/demo" variant="contained" sx={{ mt: 1, mb: 4 }} data-gtm="Blog-book-demo">
            Book a demo
        </Button>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 2, mb: 2 }}>
            Frequently asked questions
        </Typography>

        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Why do payout disputes keep happening even when I trust my therapists?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Trust doesn't produce a verified record. When therapists self-report and there's no independent confirmation,
            honest disagreements look identical to dishonest ones. The dispute isn't about character. It's about the absence
            of a record both parties can refer to.
        </Typography>

        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            Is there a way to calculate therapist payouts without a reconciliation step?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Yes, but only when sessions are verified at point of delivery. Reconciliation exists because session data and
            payout data are collected separately and matched after the fact. When the verified session record is the direct
            input to payout calculation, the reconciliation step disappears.
        </Typography>

        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            What should I look for in a session verification system?
        </Typography>
        <Typography color="text.secondary" paragraph>
            It should create the record at the point of delivery, not collect it afterwards. It should not depend entirely on
            therapist input. And it should feed directly into payout calculation without a manual step in between. If a
            system doesn't do all three, the reconciliation problem stays.
        </Typography>

        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            How do home rehab centres with verified records handle payout queries from therapists?
        </Typography>
        <Typography color="text.secondary" paragraph>
            The conversation is short. The record for each session is available, the count is visible, and any discrepancy can
            be traced to a specific session rather than a general disagreement about totals.
        </Typography>
    </Box>
);

export default PayoutDisputesHomeRehabArticle;
